import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FilterMultiSelect } from "@/components/common/FilterMultiSelect";
import { FilterSingleSelect } from "@/components/common/FilterSingleSelect";
import { Plus } from "lucide-react";
import { DEFAULT_AMENITY_CATEGORIES } from "@/types/amenity";
import {
  useAmenityCategories,
  useAmenities,
} from "@/components/pages/amenities/hooks/amenities.hooks";
import { AmenitiesFilters } from "@/services/interfaces/IAmenitiesService";
import AmenitiesTable from "@/components/pages/amenities/components/AmenitiesTable";
import { useTranslation } from "react-i18next";
import { useHotel } from "@/hooks/useHotel";

const PER_PAGE = 10;
type SortBy = NonNullable<AmenitiesFilters["sortBy"]>;

export const AmenitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [isActiveFilter, setIsActiveFilter] = useState<
    "all" | "true" | "false"
  >("all");
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const { hotelId } = useHotel();
  const { data: amenityCategories = [] } = useAmenityCategories(hotelId);
  const filters: AmenitiesFilters = useMemo(() => {
    return {
      page: currentPage,
      limit: PER_PAGE,
      categories: categories.length ? categories : undefined,
      isActive:
        isActiveFilter === "all" ? undefined : isActiveFilter === "true",
      sortBy: sortBy ?? undefined,
      sortOrder: sortOrder ?? undefined,
      hotelId,
    };
  }, [currentPage, categories, isActiveFilter, sortBy, sortOrder, hotelId]);

  const { data, isLoading } = useAmenities(filters);
  const amenities = data?.items ?? [];
  const totalPages = data?.totalPages;

  const handleSort = (field: string) => {
    const newField = field as SortBy;
    if (sortBy !== newField) {
      setSortBy(newField);
      setSortOrder("asc");
    } else {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") {
        setSortBy(null);
        setSortOrder(null);
      } else setSortOrder("asc");
    }
  };

  const categoryOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...DEFAULT_AMENITY_CATEGORIES,
          ...amenityCategories,
          ...categories,
        ]),
      ).map((value) => ({
        value,
        label: value,
      })),
    [amenityCategories, categories],
  );

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-12 p-12">
      <div className="flex flex-col gap-6">
        <div className="flex min-h-12 flex-row items-center justify-between">
          <div className="flex h-full flex-row items-center gap-x-4">
            <FilterSingleSelect
              value={isActiveFilter}
              placeholder={t("amenities.page.status")}
              className="min-w-[120px] justify-between"
              options={[
                { value: "all", label: t("amenities.page.all") },
                { value: "true", label: t("amenities.page.active") },
                { value: "false", label: t("amenities.page.inactive") },
              ]}
              onValueChange={(v) => {
                setIsActiveFilter(v as "all" | "true" | "false");
                setCurrentPage(1);
              }}
            />

            <FilterMultiSelect
              options={categoryOptions}
              placeholder={t("amenities.page.category")}
              value={categories}
              onValueChange={(vals) => {
                setCategories(vals);
                setCurrentPage(1);
              }}
              className="min-w-[160px] justify-between"
            />
          </div>

          <div className="flex flex-row items-center gap-x-4">
            <Button asChild variant="new_default">
              <Link to="/amenities/create">
                <Plus />
                {t("amenities.page.createAmenityCta")}
              </Link>
            </Button>
          </div>
        </div>

        <AmenitiesTable
          data={amenities}
          isLoading={isLoading}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages || 1}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default AmenitiesPage;
